import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Page, Dashboard } from '../../templates';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { FlexBoxVertical, FlexBoxHorizontal, BackdropPanel } from '../../atoms';
import { LanguageButton, InfoTooltip, SearchButton } from '../../molecules';
import i18n from '../../../utils/i18n';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import TestReportFilter from './Filter';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LinearProgress from '@mui/material/LinearProgress';
import palette from '../../../utils/colorPalette';
// import ChatPanel from './ChatPanel';
import { translateText } from '../../../utils/translateText';
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { LiaDownloadSolid } from "react-icons/lia";
import { MdOutlineRestartAlt } from "react-icons/md";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Select, MenuItem, FormControl, Autocomplete, TextField } from '@mui/material';
import { MdDeleteForever } from "react-icons/md";


function TestReport() {
  const apiRef = useGridApiRef();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // 저장 중 상태 추가
  const [snowflakeData, setSnowflakeData] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [lastUpdate, setLastUpdate] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  // PDF 미리보기용 상태 추가
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [formData, setFormData] = useState({
    season: '',
    certType: '',
    fabricType: '',
    fabric: '',
    vendor: '',
    passYn: '',
    fileName: '',
    remarks: ''
  });
  const language = i18n.language;
  const { t } = useTranslation();
  const [translatedColumns, setTranslatedColumns] = useState(columns(language, apiRef));
  // PDF 패널 관련 상태 수정/추가
  const [pdfViewCollapsed, setPdfViewCollapsed] = useState(false); // PDF 뷰어 접기
  const [pdfUploadCollapsed, setPdfUploadCollapsed] = useState(false); // 업로드 패널 접기
  const [isUpdating, setIsUpdating] = useState(false);
  const updateButtonRef = useRef(null);

  // PDF 파일 처리 함수
  const handlePdfFileSelect = (files) => {
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    if (pdfFiles.length > 0) {
      const file = pdfFiles[0]; // 첫 번째 PDF 파일만 처리
      setSelectedPdfFile(file);
      
      // 파일 미리보기 URL 생성
      const previewUrl = URL.createObjectURL(file);
      setPdfPreviewUrl(previewUrl);
      
      // 파일명을 자동으로 입력
      setFormData(prev => ({
        ...prev,
        fileName: file.name
      }));
      
      // 선택된 행을 PDF 미리보기로 설정
      setSelectedRow({ URL: previewUrl });
    }
  };

  // 폼 데이터 변경 핸들러
  const handleFormChange = (field, value) => {
    console.log(field, value)
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 폼 초기화 함수
  const handleFormReset = () => {
    setFormData({
      season: '',
      certType: '',
      fabricType: '',
      fabric: '',
      vendor: '',
      passYn: '',
      fileName: '',
      remarks:''
    });
    setSelectedPdfFile(null);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl('');
    }
    if (formData.fileName) {
      setSelectedRow([]);
    }
    // setSelectedRow([]);
  };

  // 수정 취소 함수 추가
  const handleFormCancel = () => {
    setIsUpdating(false);
    setFormData({
      season: '',
      certType: '',
      fabricType: '',
      fabric: '',
      vendor: '',
      passYn: '',
      fileName: '',
      remarks:''
    });
    setSelectedPdfFile(null);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl('');
    }
  };

  const deleteData = () => {
    const data = rowSelectionModel.map(item => 
      item.split('/').pop().replace('.pdf', '')
    );
    fetch('/api/fabric-test-report/delete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ data: data })
    }).then(response => response.json())
    .then(data => {
      alert(translateText({text: '데이터 삭제 완료', globalLanguage: language}));
      getFabricTestReportData();
      setSelectedRow([]);
    }).catch(error => {
      console.error('Error deleting Fabric Test Report data:', error);
      alert(translateText({text: '데이터 삭제 중 오류가 발생했습니다.', globalLanguage: language}));
    });
  };

  useEffect(() => {
    setTranslatedColumns(columns(language, apiRef).map((column) => {
      return {
        ...column, 
        headerName: translateText({
          text: column.headerName,
          globalLanguage: language,
        }),
      };
    }));
  }, [language]);


  const pdfDownload = async () => {
    const selectedSet = new Set(rowSelectionModel);
    const downloadFiles = tableData.filter((item) => selectedSet.has(item.URL));
    
    if (downloadFiles.length === 0) {
      alert(translateText({text: '다운로드할 파일을 선택해주세요.', globalLanguage: language}));
      return;
    }

    const controller = new AbortController(); // 새로운 AbortController 생성
    setAbortController(controller); // 상태에 저장

    try {
      setIsDownloading(true); // 다운로드 시작
      const response = await fetch('/api/test-report/download-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pdfs: downloadFiles.map(item => ({
            url: item.URL,
            fileName: item.FILE_NM
          }))
        }),
        signal: controller.signal // 요청에 signal 추가
      });

      if (!response.ok) {
        throw new Error('파일 다운로드 실패');
      }

      const blob = await response.blob();
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = 'test_reports.zip';  // 파일명만 지정하고 경로는 지정하지 않음
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('다운로드가 취소되었습니다.'); // 다운로드 취소 로그
      } else {
        console.error('파일 다운로드 중 오류 발생:', error);
        alert('파일 다운로드 중 오류가 발생했습니다.');
      }
    } finally {
      setIsDownloading(false); // 다운로드 종료
      setAbortController(null); // AbortController 상태 초기화
    }
  };

  const getFabricTestReportData = () => {
    fetch('/api/fabric-test-report', {
      method: 'GET', 
      headers: {
        'Content-Type': 'application/json'
      },
    })
      .then(response => response.json())
      .then(data => {
        setSnowflakeData(data)
        setTableData(data) // 초기에는 모든 데이터 표시
        setLastUpdate(new Date().toLocaleString()) // 현재 시간으로 설정
        setFilterOptions({
          sesn: createOptions(data, 'SESN'),
          certiType: createOptions(data, 'CERTIFICATE_TYPE'),
          fabric: createOptions(data, 'FABRIC'),
          passYn: createOptions(data, 'PASS_YN'),
          fabricType: createOptions(data, 'FABRIC_TYPE'),
        });
      })
      .catch(error => {
        console.error('Error fetching Fabric Test Report data:', error);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    getFabricTestReportData();
    setTimeout(() => {setIsLoading(false)}, 1000);
  }, []);


  const filterData = (options) => {
    setIsLoading(true);
    setSelectedRow([]);
    setRowSelectionModel([]);
    if (isUpdating) {
      handleFormCancel();
    }
    setIsUpdating(false);
    
    const hasValue = (value) => value && value.trim() !== '';
  
    // 비교 키 정의
    const comparisonKeys = {
        sesn: 'SESN',
        certiType: 'CERTIFICATE_TYPE',
        fabric: 'FABRIC',
        passYn: 'PASS_YN',
        fabricType: 'FABRIC_TYPE',
    };
  
    // 필터링 로직
    const filteredData = snowflakeData.filter((item) => {
      const searchValueMatch = hasValue(options.searchValue)
        ? ['FILE_NM', 'FABRIC', 'VENDOR', 'REMARKS'].some((field) =>
            item[field]?.toString().toLowerCase().includes(options.searchValue.toLowerCase())
          )
        : true;

      const comparisonMatch = Object.keys(comparisonKeys).every((key) => {
        if (!options[key] || options[key].length === 0) return true;
        
        // 모든 옵션이 선택된 경우 (전체 선택)
        if (filterOptions[key] && options[key].length === filterOptions[key].length) return true;
        
        return options[key].some(
          (option) => option.value?.toUpperCase() === String(item[comparisonKeys[key]])?.toUpperCase()
        );
      });

      return searchValueMatch && comparisonMatch;
    });
  
    setTableData(filteredData);
    setIsLoading(false);
  };

  const handleFormSave = async () => {
    if (formData.season && formData.certType && formData.fabric && formData.passYn && selectedPdfFile && formData.fileName && formData.fabricType && formData.vendor) {
      
      if (formData.season) {
        const season = formData.season.trim();
        
        // 숫자 2자리 + 영문자 1자리 형식 검증
        if (!/^\d{2}[a-zA-Z]$/.test(season)) {
          alert('시즌 형식을 확인하세요. ex) 26S');
          return;
        }
        
        // 마지막 글자를 대문자로 변환
        const lastChar = season.slice(-1).toUpperCase();
        formData.season = season.slice(0, 2) + lastChar;
        
        // 시즌 마지막 자리가 S나 F가 아니면 리턴
        if (lastChar !== 'S' && lastChar !== 'F' && lastChar !== 'N') {
          alert('알맞지 않은 시즌입니다.');
          return;
        }
      }

      
      setIsSaving(true); // 저장 시작
      
      // FormData 객체 생성 (파일 업로드용)
      const uploadFormData = new FormData();
      uploadFormData.append('season', formData.season);
      uploadFormData.append('certType', formData.certType);
      uploadFormData.append('fabricType', formData.fabricType);
      uploadFormData.append('fabric', formData.fabric);
      uploadFormData.append('vendor', formData.vendor);
      uploadFormData.append('passYn', formData.passYn);
      uploadFormData.append('fileName', formData.fileName);
      uploadFormData.append('remarks', formData.remarks);
      uploadFormData.append('pdfFile', selectedPdfFile);

      try {
        const response = await fetch('/api/fabric-test-report/save', {
          method: 'POST',
          body: uploadFormData // Content-Type 헤더는 자동으로 설정됨
        });

        const data = await response.json();
        
        if (data.success) {
          alert('업로드 완료');
          
          // 폼 초기화
          setFormData({
            season: '',
            certType: '',
            fabric: '',
            fabricType: '',
            vendor: '',
            passYn: '',
            fileName: '',
            remarks: ''
          });
          setSelectedPdfFile(null);
          setPdfPreviewUrl('');
          

          setIsLoading(true);
          getFabricTestReportData();
          // 캐시방지를 위해 URL에 타임스탬프 추가
          const urlWithTimestamp = `${data.data.url}?t=${Date.now()}`;
          setSelectedRow({URL: urlWithTimestamp});
          setIsLoading(false);

        } else {
          alert(`저장 실패: ${data.message}`);
        }
      } catch (error) {
        console.error('저장 중 오류:', error);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        setIsSaving(false); // 저장 완료
      }
    } else {
      alert('모든 필드를 입력하세요.(비고 제외)');
    }
  };

  const updateData = () => {
    if (rowSelectionModel.length === 0) {
      alert('수정할 파일을 선택해주세요.');
      return;
    }
    if (rowSelectionModel.length > 1) {
      alert('1개의 파일만 선택해주세요.');
      return;
    }
    
    // 수정 버튼의 마우스 오버 스타일 즉시 초기화
    if (updateButtonRef.current) {
      updateButtonRef.current.style.backgroundColor = 'rgba(0, 0, 0, 0)';
    }
    
    setIsUpdating(true);
    
    // 선택된 행의 데이터 가져오기
    const selectedRowId = rowSelectionModel[0];
    const selectedRowData = apiRef.current.getRow(selectedRowId);
    
    if (selectedRowData) {
      // 언어에 따라 올바른 필드명 사용
      const certTypeField = language === 'ko' ? 'CERTIFICATE_TYPE' : 'CERTIFICATE_TYPE_EN';
      const certTypeValue = selectedRowData[certTypeField] || '';
      // 인증유형 값을 옵션의 value로 매핑
      let mappedCertType = '';
      if (certTypeValue) {
        // 한국어 표시명을 영문 코드로 매핑
        const certTypeMap = {
          'GB': 'GB',
          'GB(기능성)': 'GB_F',
          'KC': 'KC',
          '아동KC': 'KC_KIDS'
        };
        mappedCertType = certTypeMap[certTypeValue] || certTypeValue;
      }
      
      // formData에 선택된 행의 데이터 설정
      setFormData({
        season: selectedRowData.SESN || '',
        certType: mappedCertType,
        fabric: selectedRowData.FABRIC || '',
        passYn: selectedRowData.PASS_YN || '',
        fileName: selectedRowData.FILE_NM || '',
        remarks: selectedRowData.REMARKS || '',
        fabricType: selectedRowData.FABRIC_TYPE || '',
        vendor: selectedRowData.VENDOR || '',
      });
      
      setSelectedRow(selectedRowData);
      setSelectedPdfFile({name: selectedRowData.FILE_NM});
    }
  };

  const handleFormUpdate = async () => {
    if (formData.season && formData.certType && formData.fabric && formData.passYn && selectedPdfFile && formData.fileName && formData.fabricType && formData.vendor) {
      
      if (formData.season) {
        const season = formData.season.trim();
        
        // 숫자 2자리 + 영문자 1자리 형식 검증
        if (!/^\d{2}[a-zA-Z]$/.test(season)) {
          alert('시즌 형식을 확인하세요. ex) 26S');
          return;
        }
        
        // 마지막 글자를 대문자로 변환
        const lastChar = season.slice(-1).toUpperCase();
        formData.season = season.slice(0, 2) + lastChar;
        
        // 시즌 마지막 자리가 S나 F가 아니면 리턴
        if (lastChar !== 'S' && lastChar !== 'F' && lastChar !== 'N') {
          alert('알맞지 않은 시즌입니다.');
          return;
        }
      }
      
      setIsSaving(true); // 저장 시작
      
      // FormData 객체 생성 (파일 업로드용)
      const updateFormData = new FormData();
      updateFormData.append('reportId', apiRef.current.getRow(rowSelectionModel[0]).REPORT_ID);
      updateFormData.append('season', formData.season);
      updateFormData.append('certType', formData.certType);
      updateFormData.append('fabric', formData.fabric);
      updateFormData.append('passYn', formData.passYn);
      updateFormData.append('fileName', formData.fileName);
      updateFormData.append('fabricType', formData.fabricType);
      updateFormData.append('vendor', formData.vendor);
      updateFormData.append('remarks', formData.remarks);
      if (selectedPdfFile && selectedPdfFile instanceof File) {
        updateFormData.append('pdfFile', selectedPdfFile);
      }

      try {
        const response = await fetch('/api/fabric-test-report/update', {
          method: 'POST',
          body: updateFormData // Content-Type 헤더는 자동으로 설정됨
        });

        const data = await response.json();
        
        if (data.success) {
          alert('수정 완료');
          
          // 폼 초기화
          setFormData({
            season: '',
            certType: '',
            fabric: '',
            passYn: '',
            fileName: '',
            remarks: '',
            fabricType: '',
            vendor: ''
          });
          setSelectedPdfFile(null);
          setPdfPreviewUrl('');
          console.log(data)
          setIsLoading(true);
          getFabricTestReportData();
          const urlWithTimestamp = `${data.data.url}?t=${Date.now()}`;
          setSelectedRow({URL: urlWithTimestamp});
          setIsLoading(false);
          setIsUpdating(false);
        } else {
          alert(`저장 실패: ${data.message}`);
        }
      } catch (error) {
        console.error('저장 중 오류:', error);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        setIsSaving(false); // 저장 완료
      }
    } else {
      alert('모든 필드를 입력하세요.(비고 제외)');
    }
  };



  return (
    <Page>
      {isLoading ? <BackdropPanel /> : null}
      <Dashboard style={{
        backgroundColor: 'white',
      }}>
        <LanguageButton position={{ top: '25px', right: '10px' }} />
        
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flex: 1,
            width: '98%',
            margin: '10px',
            marginBottom: '30px',
          }}
        >
          {filterOptions ? <TestReportFilter data={filterOptions} onSearch={filterData} /> : <></>}
          <div 
            style={{
              display: 'flex',
              height: '90%',
              position: 'relative',
              gap: '4px'
            }}
          >
            <div style={{
                      width: pdfViewCollapsed && pdfUploadCollapsed ? '100%' : pdfViewCollapsed ? '78%' : pdfUploadCollapsed ? '58%' : '40%',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative' // 추가
                      // transition 제거
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #e0e0e0',
                        flexShrink: 0,
                        height: '30px',
                      }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          {translateText({text: '성적서 목록', globalLanguage: language})}
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginRight: '12px'
                        }}>
                          {/* refresh 버튼 */}
                          <button 
                            style={{
                              cursor: isUpdating ? 'default' : 'pointer',
                              fontSize: '12px',
                              color: isUpdating ? '#9ca3af' : 'rgba(0, 0, 0, 0.54)',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              backgroundColor: 'rgba(0, 0, 0, 0)',
                              padding: '2px 5px',
                              transition: 'background-color 0.2s ease',
                              height: '28px',
                              opacity: isUpdating ? 0.5 : 1
                            }} 
                            disabled={isUpdating}
                            onMouseOver={(e) => {
                              if (!isUpdating) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!isUpdating) {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)';
                              }
                            }}
                            onClick={() => {
                              if (!isUpdating) {
                                const columns = apiRef.current.getAllColumns();
                                columns.forEach(column => {
                                  apiRef.current.sortColumn(column.field, null);
                                });
                                setRowSelectionModel([]);
                                setSelectedRow([]);
                              }
                            }}
                          >
                            <MdOutlineRestartAlt style={{ marginBottom: '-3px', fontSize: '20px' }}/>
                            <span style={{verticalAlign: '3px', marginTop: '-5px' }}>refresh</span>
                          </button>
                          {/* Excel 다운로드 버튼 */}
                          <button style={{
                            cursor: isUpdating ? 'default' : 'pointer',
                            fontSize: '19px',
                            color: isUpdating ? '#9ca3af' : 'rgba(0, 0, 0, 0.54)',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            backgroundColor: 'rgba(0, 0, 0, 0)',
                            padding: '2px 5px',
                            transition: 'background-color 0.2s ease',
                            height: '28px',
                            opacity: isUpdating ? 0.5 : 1
                          }} 
                          disabled={isUpdating}
                          onMouseOver={(e) => {
                            if (!isUpdating) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isUpdating) {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)';
                            }
                          }}
                          onClick={() => {
                            if (!isUpdating) {
                              apiRef.current.exportDataAsExcel({
                                getRowsToExport: (params) => params.apiRef.current.getAllRowIds(),
                                fileName: 'test_report',
                                exceljsPostProcess: (params) => {
                                  params.worksheet.eachRow((row) => {
                                    row.eachCell((cell) => {
                                      cell.value = cell.value === `'-` ? null : cell.value;
                                    });
                                  });
                                }
                              });
                            }
                          }}>
                            <LiaDownloadSolid style={{ marginBottom: '-3px' }}/>
                            <PiMicrosoftExcelLogoFill style={{ marginBottom: '-3px' }}/>
                          </button>
                          
                          <div style={{ 
                            display: 'flex', 
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            backgroundColor: 'rgba(0, 0, 0, 0)',
                          }}>
                            
                            <div style={{ position: 'relative', width: '130px' }}>
                              {/* PDF 다운로드 버튼 (IconButton) */}
                              <IconButton
                                onClick={() => {
                                  if (!isUpdating) {
                                    if (isDownloading) {
                                      abortController.abort();
                                      setIsDownloading(false);
                                    } else {
                                      pdfDownload();
                                    }
                                  }
                                }}
                                disabled={isUpdating}
                                sx={{
                                  padding: '2px 5px !important',
                                  width: '100%',
                                  height: '25px',
                                  borderRadius: '0',
                                  opacity: isUpdating ? 0.5 : 1,
                                  cursor: isUpdating ? 'default !important' : 'pointer',
                                  color: isUpdating ? '#9ca3af !important' : 'rgba(0, 0, 0, 0.54) !important', // 텍스트 색상 추가
                                  '&:hover': {
                                    backgroundColor: isUpdating ? 'transparent' : '#f5f5f5',
                                  },
                                  transition: 'background-color 0.1s ease',
                                  fontSize: '12px',
                                }}
                              >
                                <SystemUpdateAltIcon sx={{ 
                                  fontSize: 16, 
                                  color: isUpdating ? '#9ca3af' : '#666', // 아이콘 색상도 상태에 따라 변경
                                  marginRight: '5px' 
                                }} />
                                {isDownloading ? (
                                  <>
                                    <span style={{ margin: '0 5px 0 0' }}>{translateText({text: '다운로드 중...', globalLanguage: language})}</span> 
                                    <span style={{ color: 'rgb(255, 109, 135)', fontWeight: 'bold' }}>X</span>
                                  </>
                                ) : (
                                  <span style={{ 
                                    color: isUpdating ? '#9ca3af' : 'rgba(0, 0, 0, 0.54)' // 텍스트 색상 명시적으로 설정
                                  }}>
                                    {`${translateText({text: 'PDF 다운로드', globalLanguage: language})} (${rowSelectionModel.length})`}
                                  </span>
                                )}
                              </IconButton>
                              {isDownloading && (
                                <LinearProgress
                                  sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '2px',
                                    backgroundColor: 'rgba(97, 97, 230, 0.2)',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: palette.button.primary
                                    }
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          {/* 삭제 버튼 */}
                          <button style={{
                            cursor: isUpdating ? 'default' : 'pointer',
                            fontSize: '12px',
                            color: isUpdating ? '#9ca3af' : 'rgba(0, 0, 0, 0.54)',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            backgroundColor: 'rgba(0, 0, 0, 0)',
                            padding: '2px 5px',
                            transition: 'background-color 0.2s ease',
                            height: '28px',
                            opacity: isUpdating ? 0.5 : 1
                          }} 
                          disabled={isUpdating}
                          onMouseOver={(e) => {
                            if (!isUpdating) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isUpdating) {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)';
                            }
                          }}
                          onClick={() => {
                            if (rowSelectionModel.length === 0) {
                              alert(translateText({text: '삭제할 파일을 선택해주세요.', globalLanguage: language}));
                              return;
                            }
                            if (!isUpdating) {
                              deleteData();
                            }
                          }}
                          >
                            <span style={{verticalAlign: '1px', marginLeft: '2px' }}>{translateText({text: '삭제', globalLanguage: language})} ({rowSelectionModel.length})</span>
                          </button>
                          {/* 수정 버튼 */}
                          {rowSelectionModel.length === 1 && <button 
                            ref={updateButtonRef}
                            style={{
                              cursor: isUpdating ? 'default' : 'pointer',
                              fontSize: '12px',
                              color: isUpdating ? '#9ca3af' : 'rgba(0, 0, 0, 0.54)',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              backgroundColor: 'rgba(0, 0, 0, 0)',
                              padding: '2px 5px',
                              transition: 'background-color 0.2s ease',
                              height: '28px',
                              opacity: isUpdating ? 0.5 : 1
                            }} 
                            disabled={isUpdating}
                            onMouseOver={(e) => {
                              if (!isUpdating) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!isUpdating) {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)';
                              }
                            }}
                            onClick={() => {
                              if (!isUpdating) {
                                updateData();
                              }
                            }}
                          >
                            <span style={{verticalAlign: '1px' }}>{translateText({text: '수정', globalLanguage: language})}</span>
                          </button>}
                        </div>
                      </div>
                      
                      {/* PDF 뷰어 본체 */}
                      <div style={{
                        flex: 1,
                        width: '100%',
                        position: 'relative' // 추가
                      }}>
                        {/* 왼쪽 테이블 영역 */}
            <FlexBoxVertical style={{ 
              width: '100%',
              overflow: 'hidden'
            }}>
              <DataGridPremium
                apiRef={apiRef}
                sx={style}
                rows={tableData}
                columns={translatedColumns}
                getRowId={(row) => row['REPORT_ID']}
                hideFooter
                rowHeight={35}
                onRowClick={(params) => {
                  if (!isUpdating) { // 수정 중이 아닐 때만 클릭 가능
                    setSelectedRow(params.row);
                  }
                }}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  if (!isUpdating) { // 수정 중이 아닐 때만 선택 가능
                    setRowSelectionModel(newRowSelectionModel);
                  }
                }}
                getRowClassName={(params) => {
                  return params.row.URL.split('?')[0] === selectedRow.URL?.split('?')[0] ? 'selected-row' : '';
                }}
                initialState={{
                  pinnedColumns: {
                    right: ['refreshButton', 'excelButton'],
                  },
                }}
              />
            </FlexBoxVertical>

            {/* 수정 중일 때 오버레이 */}
            {isUpdating && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'default'
              }}>
                <div style={{backgroundColor: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 8px rgb(0 0 0 / 20%)'}}>
                  <span style={{
                    fontSize: '13px',
                    // fontWeight: '500',
                    color: '#374151'
                  }}>
                    데이터 수정 중...
                  </span>
                  </div>
              </div>
            )}
                      </div>
                    </div>
            

            {/* 오른쪽 PDF 영역 */}
                  {/* PDF 뷰어 섹션 */}
                  {!pdfViewCollapsed && (
                    <div style={{
                      width: pdfUploadCollapsed ? '50%' : '40%', 
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {/* PDF 뷰어 헤더 */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #e0e0e0',
                        flexShrink: 0,
                        height: '30px',
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151'
                          }}>
                            {translateText({text: 'PDF 뷰어', globalLanguage: language})}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <InfoTooltip
                            content={`[ ctrl + 마우스 스크롤 ] 하시면 \n 화면 축소/확대 가능합니다`}
                            style={{ marginRight: '4px', color: '#353339' }}
                          />
                          <a
                            href={`${selectedRow.URL}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                            onClick={(e) => {
                              if (!selectedRow.URL) {
                                e.preventDefault();
                                alert(translateText({text: '파일을 선택하세요.', globalLanguage: language}));
                              }
                            }}
                          >
                            <button
                            style={{
                              background: palette.button.primary,
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}>
                            {translateText({text: 'PDF 열기', globalLanguage: language})}
                            </button>
                          </a>
                          <IconButton 
                            size="small"
                            onClick={() => setPdfViewCollapsed(true)}
                            sx={{ padding: '5px', marginLeft: '40px' }}
                          >
                            <KeyboardArrowRightIcon fontSize="small" />
                          </IconButton>
                        </div>
                        
                      </div>
                      
                      {/* PDF 뷰어 본체 */}
                      <div style={{
                        flex: 1,
                        width: '100%',
                      }}>
                        {!!selectedRow && !!selectedRow.URL ? (
                          <embed
                            key={selectedRow.URL}
                            style={{
                              width: '100%',
                              height: '100%',
                              zoom: '1',
                            }}
                            src={`${selectedRow.URL}#toolbar=0`}
                            type="application/pdf"
                            onLoadStart={() => setIsLoading(true)}
                            onLoad={() => setIsLoading(false)}
                            onError={() => {
                              setIsLoading(false);
                              alert('PDF를 로드하는 데 문제가 발생했습니다. 다시 시도해주세요.');
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                            {translateText({text: '파일을 선택하세요.', globalLanguage: language})}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* PDF 뷰어 복원 버튼 */}
                  {pdfViewCollapsed && (
                    <div style={{
                      width: '40px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f8f9fa',
                      maxWidth: '800px',
                    }}>
                      <IconButton 
                        onClick={() => setPdfViewCollapsed(false)}
                        sx={{ 
                          writing: 'vertical-rl',
                          padding: '8px 4px'
                        }}
                      >
                        <div style={{
                          writingMode: 'vertical-rl',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          PDF
                        </div>
                        <KeyboardArrowLeftIcon fontSize="small" />
                      </IconButton>
                    </div>
                  )}


                  {/* 업로드 섹션 */}
                  {!pdfUploadCollapsed && (
                    <div style={{
                      width: '20%',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {/* 업로드 헤더 */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #e0e0e0',
                        flexShrink: 0,
                        height: '30px',
                      }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                         {translateText({text: '성적서 업로드', globalLanguage: language})}
                        </div>
                        <IconButton 
                          size="small"
                          onClick={() => setPdfUploadCollapsed(true)}
                        >
                          <KeyboardArrowRightIcon fontSize="small" />
                        </IconButton>
                      </div>
                      
                      {/* 업로드 본체 */}
                      <div style={{ 
                        padding: '16px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'auto'
                      }}>
                        

                        {/* 데이터 입력 폼 */}
                        <div>
                          {[
                            { key: 'season', label: '시즌', type: 'input', placeholder: 'ex) 26S / 26N / 26F' },
                            { 
                              key: 'certType', 
                              label: '인증유형', 
                              type: 'select', 
                              options: getCertTypeOptions(language) 
                            },
                            { 
                              key: 'fabricType', 
                              label: '패브릭타입', 
                              type: 'select', 
                              options: [{ value: '', label: translateText({text: '패브릭타입 선택', globalLanguage: language}) },{ value: 'WOVEN', label: 'WOVEN' }, { value: 'KNIT', label: 'KNIT'}, {value: 'SWEATER', label: 'SWEATER' }],
                            },
                            { key: 'fabric', label: '패브릭', type: 'input', placeholder: translateText({text: '패브릭 입력', globalLanguage: language})},
                            { key: 'vendor', label: '협력사', type: 'input', placeholder: translateText({text: '협력사 입력', globalLanguage: language})},
                            { key: 'passYn', label: '패스여부', type: 'select', options: [
                              { value: '', label: translateText({text: 'Y/N 선택', globalLanguage: language}) },
                              { value: 'Y', label: 'Y' },
                              { value: 'N', label: 'N' }
                            ] },
                            { key: 'fileName', label: '파일명', type: 'input', placeholder: translateText({text: '파일 선택 시 자동 입력', globalLanguage: language}), disabled: true },
                            { key: 'remarks', label: '비고 (선택)', type: 'input', placeholder: translateText({text: '비고 입력', globalLanguage: language})}
                          ].map((field, index) => (
                            <div key={field.key} style={{
                              display: 'flex',
                              flexDirection: 'column',
                              marginBottom: '12px',
                            }}>
                              <div style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#374151',
                                marginBottom: '4px'
                              }}>
                                {translateText({text: field.label, globalLanguage: language})}
                              </div>
                              <div>
                                {field.type === 'input' ? (
                                  <input
                                    type="text"
                                    value={formData[field.key]}
                                    disabled={field.disabled}
                                    onChange={(e) => handleFormChange(field.key, e.target.value)}
                                    style={{
                                      width: '100%',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '4px',
                                      padding: '6px 8px',
                                      fontSize: '12px',
                                      backgroundColor: field.disabled ? '#f9fafb' : 'white',
                                      color: field.disabled ? '#9ca3af' : '#374151',
                                      outline: 'none',
                                      boxSizing: 'border-box'
                                    }}
                                    placeholder={translateText({text: field.placeholder, globalLanguage: language})}
                                  />
                                ) : field.type === 'autocomplete' ? (
                                  <Autocomplete
                                    value={formData[field.key]}
                                    onChange={(event, newValue) => handleFormChange(field.key, newValue?.value || newValue)}
                                    options={filterOptions?.[field.key] || []}
                                    // getOptionLabel={(option) => {
                                    //   if (typeof option === 'string') return option;
                                    //   // 언어가 영어일 때는 label_en을 사용, 그렇지 않으면 label 사용
                                    //   if (language === 'en' && option?.label_en) {
                                    //     return option.label_en;
                                    //   }
                                    //   return option?.label || option?.value || '';
                                    // }}
                                    isOptionEqualToValue={(option, value) => {
                                      if (typeof option === 'string' && typeof value === 'string') {
                                        return option === value;
                                      }
                                      return option?.value === value;
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder={translateText({text: field.placeholder, globalLanguage: language})}
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            fontSize: '12px',
                                            height: '30px',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                              border: '1px solid #e5e7eb !important',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                              border: '1px solid #e5e7eb !important',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                              border: '1px solid #e5e7eb !important',
                                            },
                                            '& .MuiInputBase-input': {
                                              padding: '0px 0px',
                                            }
                                          }
                                        }}
                                      />
                                    )}
                                    ListboxProps={{
                                      style: {
                                        fontSize: '12px'
                                      }
                                    }}
                                  />
                                ) : (
                                  <FormControl fullWidth>
                                    <Select
                                      value={formData[field.key]}
                                      onChange={(e) => handleFormChange(field.key, e.target.value)}
                                      displayEmpty
                                      sx={{
                                        fontSize: '12px',
                                        height: '30px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          border: '1px solid #e5e7eb !important',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                          border: '1px solid #e5e7eb !important',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                          border: '1px solid #e5e7eb !important',
                                        },
                                        '& .MuiSelect-select': {
                                          padding: '6px 8px',
                                        }
                                      }}
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            '& .MuiMenuItem-root': {
                                              fontSize: '12px', // 옵션들 글씨 크기를 10px로 작게
                                            }
                                          }
                                        }
                                      }}
                                    >
                                      {field.options.map((option, optIndex) => (
                                        <MenuItem key={optIndex} value={optIndex === 0 ? '' : option.value}>
                                          {option.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* PDF 업로드 영역 */}
                        <div style={{
                          border: '3px dashed #e0e7ff',
                          borderRadius: '12px',
                          padding: '20px',
                          marginBottom: '16px',
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)';
                          e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = '#e0e7ff';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = '#e0e7ff';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
                          e.currentTarget.style.transform = 'scale(1)';
                          const files = Array.from(e.dataTransfer.files);
                          handlePdfFileSelect(files);
                        }}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf';
                          input.multiple = true;
                          input.onchange = (e) => {
                            const files = Array.from(e.target.files);
                            handlePdfFileSelect(files);
                          };
                          input.click();
                        }}>
                          <div style={{
                            fontSize: '32px',
                            marginBottom: '8px',
                          }}>
                            📄
                          </div>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1e293b',
                            marginBottom: '4px',
                          }}>
                            {translateText({text: 'PDF 파일 업로드', globalLanguage: language})}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}>
                            {selectedPdfFile ? (
                              <>
                                <span>{selectedPdfFile.name}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // 부모 클릭 이벤트 방지
                                    setSelectedPdfFile(null);
                                    setPdfPreviewUrl('');
                                    setSelectedRow([]);
                                    formData.fileName = '';
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ef4444',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    padding: '2px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '20px',
                                    height: '20px',
                                    fontWeight: '600',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = 'rgba(152, 148, 149, 0.14)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  x
                                </button>
                              </>
                            ) : (
                              translateText({text: '파일을 드래그하거나 클릭하세요', globalLanguage: language})
                            )}
                          </div>
                        </div>

                        {/* 버튼 영역 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '8px',
                          flexShrink: 0,
                          marginTop: '16px'
                        }}>
                          <button
                            onClick={isUpdating ? handleFormUpdate : handleFormSave}
                            disabled={isSaving} // 저장 중일 때 비활성화
                            style={{
                              background: isSaving 
                                ? '#cccccc' // 비활성화 시 회색
                                : palette.button.primary,
                              color: isSaving ? '#666666' : 'white', // 비활성화 시 회색, 활성화 시 흰색
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: isSaving ? 'not-allowed' : 'pointer', // 비활성화 시 커서 변경
                              fontSize: '12px',
                              fontWeight: '600',
                              opacity: isSaving ? 0.6 : 1, // 비활성화 시 투명도 조절
                            }}>
                            {isSaving 
                              ? translateText({text: '저장 중...', globalLanguage: language})
                              : translateText({text: '저장', globalLanguage: language})
                            }
                          </button>
                          <button 
                            onClick={isUpdating ? handleFormCancel : handleFormReset}
                            style={{
                              background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)', 
                              color: '#6b7280', 
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                            }}>
                            {translateText({text: isUpdating ? '취소' : '초기화', globalLanguage: language})}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 업로드 복원 버튼 */}
                  {pdfUploadCollapsed && (
                    <div style={{
                      width: '40px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f8f9fa'
                    }}>
                      <IconButton 
                        onClick={() => setPdfUploadCollapsed(false)}
                        sx={{ 
                          writing: 'vertical-rl',
                          padding: '8px 4px'
                        }}
                      >
                        <div style={{
                          writingMode: 'vertical-rl',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          {translateText({text: '업로드', globalLanguage: language})}
                        </div>
                        <KeyboardArrowLeftIcon fontSize="small" />
                      </IconButton>
                    </div>
                  )}
                

                {chatOpen && (
                  <div
                    style={{
                      position: 'fixed',
                      right: 200,
                      top: 100,
                      width: '300px',
                      height: '80vh',
                      zIndex: 1000,
                      pointerEvents: 'auto'
                    }}
                  >
                    {/* <ChatPanel onClose={() => setChatOpen(false)} selectedFile={selectedRow} /> */}
                  </div>
                )}

          </div>
        </div>
      </Dashboard>
    </Page>
  );
}


export default TestReport;

const columns = (language, apiRef) => [
  {
    field: 'SESN',
    headerName : '시즌',
    width : 80
  },
  {
    field: language === 'ko' ? 'CLASSIFICATION' : 'CLASSIFICATION_EN',
    headerName: '원단/제품',
    width: 80,
  },
  {
    field: language === 'ko' ? 'CERTIFICATE_TYPE' : 'CERTIFICATE_TYPE_EN',
    headerName: '인증유형',
    width: 100,
  },
  {
    field: 'FABRIC_TYPE',
    headerName: '패브릭 타입',
    width: 100,
  },
  {
    field: 'FABRIC',
    headerName: '패브릭',
    width: 200,
  },
  {
    field: 'PASS_YN',
    headerName: '패스여부',
    width: 80,
  },
  {
    field: 'FILE_NM',
    headerName: '파일명',
    width: 300,
  },
  {
    field: 'VENDOR',
    headerName: '협력사',
    width: 200,
  },
  {
    field: 'REMARKS',
    headerName: '비고',
    width: 300,
  },
];

const style = {
  border: 'none',
  width: '100%',
  fontSize: '0.7rem',
  '& .MuiDataGrid-columnHeader': {
    // backgroundColor: 'rgba(229, 221, 243, 0.12) !important',
    // height: '40px !important',
    backgroundColor: 'white !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
    fontSize: '0.7rem',
  },
  '& .Mui-selected': {
    backgroundColor: 'white !important',
  },
  '& .MuiCheckbox-root': {
    color: '#666666',
    '&.Mui-checked': {
      color: '#666666',
    }
  },
  '& .MuiDataGrid-row.selected-row': {
      backgroundColor: 'rgba(224, 236, 251, 0.76) !important',
    },
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
    backgroundColor: 'white !important',
    '&:hover': {
      backgroundColor: 'rgba(195, 196, 197, 0.2) !important',
    }
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none'
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none'
  },
  '& .MuiDataGrid-cell--pinnedRight': {
    borderLeft: 'none',
    position: 'unset',
    backgroundColor: 'white !important',
  },
  '& .MuiDataGrid-filler--pinnedRight': {
    borderLeft: 'none',
    position: 'unset',
  },
  '& .MuiDataGrid-columnHeader--withLeftBorder': {
    borderLeft: 'none',
  },
};

const createOptions = (data, key, filterFn = (value) => value !== null) => {
  const options = Array.from(
    new Set(
      data.map((item) => (item[key] === null ? '-' : item[key])).filter(filterFn)
    )
  ).map((value) => {
    const item = data.find(item => item[key] === value);
    return {
      value: value,
      label: value,
      label_en: item?.[key + '_EN'] ? `${item[key + '_EN']}` : value
    };
  });


  if ( key === 'CERTIFICATE_TYPE') {
    const distinctOptions = Array.from(
      new Map(options.map((item) => [item.label_en, item])).values()
    );
    return distinctOptions.sort((a, b) => {
      if (a.label_en === '-') return -1;
      if (b.label_en === '-') return 1;
      return a.label_en.localeCompare(b.label_en);
    });
  } else {
    const distinctOptions = Array.from(
      new Map(options.map((item) => [item.value, item])).values()
    );
    return distinctOptions.sort((a, b) => {
      if (a.value === '-') return -1;
      if (b.value === '-') return 1;
      return a.value.localeCompare(b.value);
  });
  }
};

// 인증유형 옵션을 반환하는 함수 (value-label 구조)
const getCertTypeOptions = (language) => {
  const placeholder = translateText({text: '인증유형 선택', globalLanguage: language});
  
  // 기본 플레이스홀더
  const defaultOption = { value: '', label: placeholder };
  
  // 영문 코드는 동일하지만, 표시되는 라벨만 언어별로 다름
  const options = [
    { value: 'GB', label: language === 'en' ? 'GB' : 'GB' },
    { value: 'GB_F', label: language === 'en' ? 'GB_F' : 'GB(기능성)' },
    { value: 'KC', label: language === 'en' ? 'KC' : 'KC' },
    { value: 'KC_KIDS', label: language === 'en' ? 'KC_KIDS' : '아동KC' }
  ];
  
  return [defaultOption, ...options];
};